import * as React from 'react';
import { Minus, Plus } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface NumberStepperProps {
  id?: string;
  value: number;
  min: number;
  max: number;
  step?: number;
  disabled?: boolean;
  /** Human-readable subject for button aria-labels: "Decrease {label}" / "Increase {label}". */
  label: string;
  /** aria-label for the value input itself. When omitted, the input relies on an external <label htmlFor>. */
  ariaLabel?: string;
  ariaDescribedby?: string;
  ariaInvalid?: boolean;
  onChange: (next: number) => void;
  className?: string;
  testId?: string;
  decrementTestId?: string;
  incrementTestId?: string;
  inputClassName?: string;
}

export const NumberStepper = React.forwardRef<HTMLInputElement, NumberStepperProps>(
  (
    {
      id,
      value,
      min,
      max,
      step = 1,
      disabled,
      label,
      ariaLabel,
      ariaDescribedby,
      ariaInvalid,
      onChange,
      className,
      testId,
      decrementTestId,
      incrementTestId,
      inputClassName,
    },
    ref,
  ) => {
    'use no memo';
    const atMin = value <= min;
    const atMax = value >= max;

    const clampedNext = (candidate: number) =>
      Math.max(min, Math.min(max, candidate));

    const decrement = () => {
      if (disabled || atMin) return;
      onChange(clampedNext(value - step));
    };
    const increment = () => {
      if (disabled || atMax) return;
      onChange(clampedNext(value + step));
    };

    return (
      <div
        data-testid={testId}
        className={cn(
          'input-recessed inline-flex items-stretch overflow-hidden',
          disabled && 'opacity-50',
          className,
        )}
        aria-disabled={disabled || undefined}
      >
        <button
          type="button"
          aria-label={`Decrease ${label}`}
          data-testid={decrementTestId}
          aria-disabled={(disabled || atMin) || undefined}
          onClick={decrement}
          className="touch-target-pseudo relative h-8 w-8 flex items-center justify-center text-muted-foreground hover:text-primary-text aria-disabled:opacity-40 aria-disabled:cursor-not-allowed aria-disabled:pointer-events-none active:translate-y-px transition-colors after:absolute after:inset-x-0 after:-inset-y-2 after:min-h-[44px] after:content-[''] sm:after:hidden"
        >
          <Minus className="h-3.5 w-3.5" aria-hidden />
        </button>
        <input
          ref={ref}
          id={id}
          type="text"
          inputMode="decimal"
          readOnly
          aria-label={ariaLabel}
          aria-disabled={disabled || undefined}
          aria-valuemin={min}
          aria-valuemax={max}
          aria-valuenow={value}
          aria-invalid={ariaInvalid || undefined}
          aria-describedby={ariaDescribedby}
          tabIndex={disabled ? -1 : 0}
          value={String(value)}
          onChange={() => {}}
          onKeyDown={(e) => {
            if (disabled) return;
            if (e.key === 'ArrowUp' || e.key === 'PageUp') {
              e.preventDefault();
              increment();
            } else if (e.key === 'ArrowDown' || e.key === 'PageDown') {
              e.preventDefault();
              decrement();
            } else if (e.key === 'Home') {
              e.preventDefault();
              onChange(min);
            } else if (e.key === 'End') {
              e.preventDefault();
              onChange(max);
            }
          }}
          className={cn(
            'number-input-inner w-12 h-8 bg-transparent font-mono tabular-nums text-[16px] sm:text-xs text-foreground text-center outline-none focus:outline-none caret-transparent cursor-default disabled:cursor-not-allowed',
            inputClassName,
          )}
        />
        <button
          type="button"
          aria-label={`Increase ${label}`}
          data-testid={incrementTestId}
          aria-disabled={(disabled || atMax) || undefined}
          onClick={increment}
          className="touch-target-pseudo relative h-8 w-8 flex items-center justify-center text-muted-foreground hover:text-primary-text aria-disabled:opacity-40 aria-disabled:cursor-not-allowed aria-disabled:pointer-events-none active:translate-y-px transition-colors after:absolute after:inset-x-0 after:-inset-y-2 after:min-h-[44px] after:content-[''] sm:after:hidden"
        >
          <Plus className="h-3.5 w-3.5" aria-hidden />
        </button>
      </div>
    );
  },
);
NumberStepper.displayName = 'NumberStepper';
